import { FC, useContext, useState } from "react";
import CopyIcon from "../../../images/cognito_auth/copy.svg";
import { ApplicationContext } from "../../../components/ApplicationProvider";
import {
    Container,
    EntityFieldWrapper,
    EntityLabelRow,
    EntityValueBox,
    Icon,
    LabelText,
    ReturnFieldWrapper,
    ReturnLabelRow,
    ReturnValueBox,
    Title,
    Tooltip,
    ValueText,
} from "./GrabcadDetailsStyles";
import { AuthSettings } from "@grabcad/company-server-shared/dist/types";

const useCopyToClipboard = () => {
    const [visible, setVisible] = useState(false);

    const copy = (text: string) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                setVisible(true);
                setTimeout(() => setVisible(false), 1500);
            })
            .catch((err) => console.error("Failed to copy text:", err));
    };

    return { visible, copy };
};
interface CopyableFieldProps {
    label: string;
    value: string;
    wrapper?: "entity" | "return";
    disabled?: boolean;
}

const CopyableField: FC<CopyableFieldProps> = ({ label, value, wrapper = "entity", disabled }) => {
    const { visible, copy } = useCopyToClipboard();
    const { t } = useContext(ApplicationContext);

    const handleCopy = () => {
        if (!disabled) {
            copy(value);
        }
    };

    const LabelRow = wrapper === "entity" ? EntityLabelRow : ReturnLabelRow;
    const ValueBox = wrapper === "entity" ? EntityValueBox : ReturnValueBox;
    const FieldWrapper = wrapper === "entity" ? EntityFieldWrapper : ReturnFieldWrapper;

    const tooltipId = `${wrapper}-tooltip`;

    return (
        <FieldWrapper data-testid={`${wrapper}-field-wrapper`}>
            <LabelRow data-testid={`${wrapper}-label-row`}>
                <LabelText>{label}</LabelText>
                <Icon
                    onClick={handleCopy}
                    data-testid={`${wrapper}-copy-icon`}
                    style={{
                        opacity: disabled ? 0.4 : 1,
                        pointerEvents: disabled ? "none" : "auto",
                    }}
                >
                    <img src={CopyIcon} alt="Copy" />
                </Icon>
                {!disabled && visible && (
                    <Tooltip id={tooltipId} visible={visible} data-testid={`${wrapper}-tooltip`}>
                        {t("configure_sso_setting.grabcad_details.copied")}
                    </Tooltip>
                )}
            </LabelRow>
            <ValueBox data-testid={`${wrapper}-value-box`}>
                <ValueText>{disabled ? "" : value}</ValueText>
            </ValueBox>
        </FieldWrapper>
    );
};

export interface GrabcadDetailsProps {
    authSettings?: AuthSettings;
    isEditable?: boolean;
}

const GrabcadDetails: FC<GrabcadDetailsProps> = ({ authSettings, isEditable }) => {
    const { t } = useContext(ApplicationContext);

    const entityId = `urn:amazon:cognito:sp:${authSettings?.grabcadDetails?.userPoolId}`;
    const returnUrl = `${authSettings?.grabcadDetails?.authDomainUrl}/saml2/idpresponse`;

    const fields: { label: string; value: string; wrapper: "entity" | "return" }[] = [
        {
            label: t("configure_sso_setting.grabcad_details.entity_id"),
            value: entityId,
            wrapper: "entity",
        },
        {
            label: t("configure_sso_setting.grabcad_details.return_url"),
            value: returnUrl,
            wrapper: "return",
        },
    ];

    return (
        <Container>
            <Title>{t("configure_sso_setting.grabcad_details.title")}</Title>
            {fields.map((field) => (
                <CopyableField
                    key={`${field.wrapper}-${field.label}`}
                    label={field.label}
                    value={field.value}
                    wrapper={field.wrapper}
                    disabled={!isEditable}
                />
            ))}
        </Container>
    );
};

export default GrabcadDetails;
